import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Action, ComponentType, FormStatusEnum, WFSaveOptions } from 'src/app/common/enumerations/enumerations';
import { MasterControlComponent } from 'src/app/controls/checklist-components/master-control/master-control.component';
import { WfTaskLocalResource, WfSignature, WFSectionLocalResource, WFStepLocalResource, SaveTaskResource } from 'src/app/components/workflow/workflow';
import { Checklist, ChecklistTemplate } from '../../../checklists';
import { ChecklistsService } from '../../../checklists.service';
import { ChecklistsRefreshById } from '../../../store/checklists/checklists.action';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { DuplicateAction, TaskDuplicateResource } from 'src/app/controls/checklist-components/button-signature-duplicate/button-signature-duplicate.component';
import { TasksUpdate } from '../../../store/tasks/task.action';
import { getBooleanValue } from 'src/app/modules/libs/utils';
import { SignalRAction } from '../../../../../services/signal-r/signalr-enums';

@Component({
  selector: "checklist-task",
  templateUrl: "./checklist-task.component.html",
  styleUrls: ["./checklist-task.component.scss"],
})
export class ChecklistTaskComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentChecklist?: Checklist | null;
  @Input() currentChecklistTemplate?: ChecklistTemplate;
  @Input() task!: WfTaskLocalResource;
  @Input() disabled!: boolean;
  @Input() precheck?: boolean;

  @Output() updateTask = new EventEmitter<WfTaskLocalResource>();
  @Output() taskValueChanged = new EventEmitter<WfTaskLocalResource>();
  @Output() checkShared = new EventEmitter<any>();
  @Output() itemCountChanged = new EventEmitter<number>();

  @ViewChild(MasterControlComponent)
  masterControl!: MasterControlComponent;

  checklist!: Checklist;
  checklists!: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;
  wfTaskLocals?: WfTaskLocalResource[];
  wfTaskLocals$!: Observable<WfTaskLocalResource[]>;
  wfTaskLocalsSubs!: Subscription;

  s!: WfSignature;

  color!: string;

  section?: WFSectionLocalResource;
  step?: WFStepLocalResource;

  formStatus = FormStatusEnum;
  saving!: boolean;

  /** Set for a tick to rebuild the control after the server refuses a change. */
  resetting = false;

  componentType = ComponentType;

  constructor(
    protected override injector: Injector,
    private checklistsService: ChecklistsService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadWFTaskLocals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initRefresh();
  }

  /**
   * Keeps the rendered row synchronized when another browser updates the task through SignalR.
   */
  loadWFTaskLocals(): void {
    this.wfTaskLocals$ = this.store.select(state => state.WFTaskLocals.data);
    this.wfTaskLocalsSubs = this.wfTaskLocals$.subscribe(data => {
      this.wfTaskLocals = data;
      const updatedTask = this.wfTaskLocals?.find(t => t.id == this.task?.id);
      if (updatedTask) {
        // Keep rendered checklist rows aligned with SignalR task updates from other users.
        this.syncCurrentChecklistTask(updatedTask);
        this.task = this.getCurrentChecklistTask(updatedTask) ?? updatedTask;
        this.initRefresh();
      }
    });
  }

  initRefresh() {
    this.task = this.getCurrentChecklistTask(this.task) ?? this.task;
    this.step = this.task?.wfStepLocal;
    this.section = this.step?.wfSectionLocal;
    if (this.section && this.step && this.task) {
      this.step.wfSectionLocal = this.section;
      this.task.wfStepLocal = this.step;
    }
    this.refreshSignature();
  }

  /**
   * Finds the matching task in the latest checklist tree after store refreshes.
   * @param task Rendered task instance currently held by the reused row component.
   * @returns Fresh task from the checklist hierarchy when available.
   */
  private getCurrentChecklistTask(task?: WfTaskLocalResource): WfTaskLocalResource | undefined {
    if (!task?.id)
      return undefined;

    // Reused task rows need to pull from the refreshed checklist after status-reset updates.
    return this.currentChecklist?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(section => section.wfStepLocals ?? [])
      ?.flatMap(step => step.wfTaskLocals ?? [])
      ?.find(currentTask => currentTask.id === task.id);
  }

  /**
   * Copies a SignalR task update into the checklist hierarchy used by child controls.
   * @param task Latest task resource received from the task store or API response.
   */
  private syncCurrentChecklistTask(task: WfTaskLocalResource): void {
    const currentTask = this.getCurrentChecklistTask(task);
    if (!currentTask)
      return;

    // Keep existing object references alive while replacing stale task fields.
    const currentStep = task.wfStepLocal ?? currentTask.wfStepLocal;
    Object.assign(currentTask, task);
    currentTask.wfStepLocal = currentStep;
  }

  /**
   * Applies a saved or signed task to every local task reference used by the checklist UI.
   * @param task Updated task returned by the API.
   */
  private applyTaskUpdate(task: WfTaskLocalResource): void {
    task.required = true;
    task.isCompleted = this.utils.taskComplete(task);

    this.task = task;
    this.refreshSignature(task);
  }

  override ngOnDestroy() {
    this.checklistsSubs?.unsubscribe();
    this.wfTaskLocalsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  refreshSignature(t: WfTaskLocalResource | null = null) {
    if (t)
      this.task = t;
    if (this.task) {
      this.syncCurrentChecklistTask(this.task);
      this.s = this.getSignature(this.task);
    }
    this.saving = false;
  }

  /**
   * Builds the rendered signature from the latest workflow task state.
   * @param t Local workflow task used by the checklist control.
   * @returns Signature data consumed by master-control.
   */
  getSignature(t: WfTaskLocalResource): WfSignature {
    const latestWfTask = t.wfTasks?.at(-1);
    // Keep checkbox state, initials, and tooltip aligned after status-reset refreshes.
    const signed = latestWfTask?.signedBy != null;
    const disableInsteadOfHide = !!this.utils.JSONparse(t.configuration)?.hideDisable;
    // Only conditional Hide-mode tasks should disappear when their checklist status disables the step.
    const required = t.required !== false &&
      (!this.disabled || !t.condition || disableInsteadOfHide);
    const signature: WfSignature = {
      id: t.id ?? 0,
      type: t.componentID,
      code: t.code,
      number: t.component?.numericHeader ? t.name : null,
      required,
      name: t.name,
      question: t.name3,
      question2: t.name2,
      question3: t.name4,
      options: this.utils.JSONparse(t.options) ? this.utils.JSONparse(t.options) : [],
      condition: t.condition,
      sigCondition: t.sigCondition,
      configuration: t.configuration,
      taskId: latestWfTask?.id,
      disabled: t.disabled || this.disabled || this.disableUser(t) || this.sigConditionBlocked(t),
      disableEdit: t.disabled || this.disabled || this.disableUser(t),
      enabled: true,
      value: t.value,
      value2: t.numericValue,
      textValue: this.utils.getTextValue(t.text),
      roles: t.wfRoleLocals?.map(x => x.roleID),
      roleCodes: t.wfRoleLocals?.map(x => x.role?.code).join('/'),
      approveAction: t.wfActionLocals?.find(x => x.action == Action.Approve) != null && !signed,
      approved: signed,
      approveActionId: t.wfActionLocals?.find((x) => x.action == Action.Approve)?.id,
      unapproveAction: t.wfActionLocals?.find((x) => x.action == Action.Unapprove) != null &&
        signed &&
        latestWfTask?.signedBy?.id == this.getCurrentUser()?.id,
      unapproveActionId: t.wfActionLocals?.find(x => x.action == Action.Unapprove)?.id,
      disapproveAction: t.wfActionLocals?.find((x) => x.action == Action.Disapprove) != null,
      disapproveActionId: t.wfActionLocals?.find(x => x.action == Action.Disapprove)?.id,
      approvedBy: signed ? latestWfTask?.signedBy as User : null,
      approvedOn: signed ? latestWfTask?.signedOn : null,
      visible: latestWfTask?.required,
      scheduleTypeId: t.scheduleType,
      scheduleResourceId: t.scheduleResourceId,
      booleanValue: getBooleanValue(t.text ?? undefined),
      // Use the synchronized checklist task so shared shielding updates refresh child controls.
      wfTaskLocal: this.getCurrentChecklistTask(t) ?? t,
      previousTasksCompleted: t.previousTasksCompleted
    };
    if (this.s?.configuration) {
      this.color = JSON.parse(this.s?.configuration).color
        ? JSON.parse(this.s?.configuration).color
        : "primary";
    }
    return signature;
  }

  disableUser(t: WfTaskLocalResource) {
    if (!this.checklistsService.participants || t.code == "PARTICIPANT") {
      if (!t.wfRoleLocals?.length) return false;
      return !this.hasRoles(t.wfRoleLocals.map(r => r.roleID));
    } else {
      return !this.checklistsService.participants.map(x => x.id).includes(this.getCurrentUser()?.id);
    }
  }

  /**
   * True when the current user already signed the task this one is mutually exclusive with, and so
   * must not sign this one as well.
   *
   * This only mirrors the rule in the UI. The authoritative check lives on the server in
   * SigConditionHelper, reached from ChecklistService.UpdateWFTaskLocalValues and
   * WorkFlowTaskSignApproveService.Sign.
   * @param t The task whose signature condition is being evaluated.
   */
  sigConditionBlocked(t: WfTaskLocalResource) {
    return this.sigConditionConflict(t) != null;
  }

  /**
   * The Tag of the already signed task that blocks this one, or null when signing is allowed.
   * @param t The task whose signature condition is being evaluated.
   */
  sigConditionConflict(t: WfTaskLocalResource): string | null {
    const tag = this.normalizeTag(t.sigCondition);
    const userId = this.getCurrentUser()?.id;

    // Both operands are optional, so a loose comparison of two undefined values would read as a
    // match and lock every task carrying a condition. Bail out unless there is a user to compare.
    if (!tag || userId == null) return null;

    // A signature the user already owns has to stay removable, otherwise a pair that both ended up
    // signed would deadlock with neither side clearable.
    if (this.signedByUser(t, userId)) return null;

    const conflict = this.sigConditionScope(t)
      .find(x => x.id != t.id && this.normalizeTag(x.code) == tag && this.signedByUser(x, userId));

    return conflict?.code ?? null;
  }

  /**
   * The tasks a signature condition is allowed to resolve against: those in the checklist being
   * executed. Tags are only unique within one checklist, and the WFTaskLocals store accumulates
   * every checklist opened this session, so an unscoped lookup can match a task from another one.
   * @param t The task whose signature condition is being resolved.
   */
  private sigConditionScope(t: WfTaskLocalResource): WfTaskLocalResource[] {
    const checklistTasks = this.currentChecklist?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(s => s.wfStepLocals ?? [])
      ?.flatMap(s => s.wfTaskLocals ?? []) ?? [];

    if (!checklistTasks.length) {
      // No checklist tree to scope by, so fall back to the store. The server enforces the rule
      // regardless of what the UI can see from here.
      return this.wfTaskLocals ?? [t];
    }

    const ids = new Set(checklistTasks.map(x => x.id));
    const stored = this.wfTaskLocals?.filter(x => ids.has(x.id)) ?? [];

    // Prefer the store, which SignalR keeps current, and use the checklist tree until it loads.
    // ngOnChanges runs getSignature before ngOnInit subscribes, so without this the first render
    // would evaluate against nothing and leave a task that should be locked enabled.
    return stored.length ? stored : checklistTasks;
  }

  /**
   * Whether any instance of the task carries the given user's signature. Iterable steps and
   * amendments give one task local more than one signable instance, so all of them are checked.
   */
  private signedByUser(t: WfTaskLocalResource, userId: number) {
    return t.wfTasks?.some(w => w.signedBy?.id != null && w.signedBy.id == userId) ?? false;
  }

  /**
   * Tags are compared without surrounding whitespace and case insensitively, matching how
   * ConditionParser resolves them and how the server normalises them.
   */
  private normalizeTag(tag?: string | null) {
    const trimmed = tag?.trim();
    return trimmed ? trimmed.toUpperCase() : null;
  }

  disabledClose(s: WfSignature) {
    return s.type == ComponentType.CloseButton && !this.checklistsService.readyToClose() && this.currentChecklist?.checklistTemplate?.templateType?.allSignaturesRequired;
  }

  sign(e: any, taskId?: number, actionId?: number, isASCC: boolean = false) {
    if (taskId && actionId) {
      this.saving = true;
      this.checklistsService.sign(taskId, actionId, null, null, isASCC, this.task.updatedOn ?? null).toPromise().then(async task => {
        if (task) {
          // Keep the visible task and the checklist tree current after sign/un-sign actions.
          this.applyTaskUpdate(task);
          this.publishTask(this.task);
          this.updateTask.emit(this.task);
          if (this.currentChecklist && this.task.code && task.isCompleted)
            await this.checklistsService.processTags(this.currentChecklist, this.task);
        }
      })
        .catch(async (error) => {
          console.log(error);
          this.reportSaveError(error);
          await this.restoreFromServer();
        }).finally(() =>
          setTimeout(() => {
          }, 600));
    }
  }

  duplicate(data: TaskDuplicateResource) {
    this.saving = true;
    if (this.currentChecklist)
      this.store.dispatch(new ChecklistsRefreshById(this.currentChecklist.id));
  }

  change(e: any, id?: number): void {
    if (!id || !this.task) {
      return;
    }

    switch (this.task.component?.id) {
      case ComponentType.CheckboxAndRadio:
      case ComponentType.CheckboxRadioLocation:
      case ComponentType.PlaceholderCondition:
      case ComponentType.MultipleStatusChange:
        this.handleNumericChange(id, e);
        return;

      case ComponentType.RadMonitorDueDate:
        this.handleRadMonitorDueDateChange(id, e);
        return;

      default:
        this.handleTextChange(id, e);
        return;
    }
  }

  /**
   * Updates a numeric task value and notifies the section before persisting it.
   * This allows dependent conditional tasks to react without waiting for a checklist refresh.
   * @param id Task resource identifier.
   * @param value Selected numeric value.
   */
  private handleNumericChange(id: number, value: number): void {
    // Picking a radio option applies the user's initials to the checkbox on its own, which is a
    // signature just like ticking the box, so the signature condition is evaluated here too rather
    // than only on the explicit sign path.
    const conflict = this.sigConditionConflict(this.task);
    if (conflict) {
      this.alert.error(this.sigConditionMessage(conflict));
      this.refreshSignature();
      return;
    }

    this.task!.numericValue = value;

    const latestWfTask = this.task?.wfTasks?.at(-1);
    if (latestWfTask)
      latestWfTask.signedBy = this.getCurrentUser(true) as User;

    // Re-evaluate sibling conditions immediately while the save request runs.
    this.taskValueChanged.emit(this.task);

    const saveTask: SaveTaskResource = {
      id,
      numericValue: value,
      field: 'numericValue',
      option: WFSaveOptions.MANUAL,
    };

    this.saveTask(saveTask, false);
  }

  /**
   * The wording shown when a signature condition blocks a signature, matching the message the
   * server returns so the user sees the same explanation whichever side catches it.
   * @param conflictingTag The Tag of the already signed task that blocks this one.
   */
  private sigConditionMessage(conflictingTag: string) {
    return `You already signed the task tagged '${conflictingTag}'. This task requires a different person's signature.`;
  }

  private handleRadMonitorDueDateChange(id: number, value: string): void {
    this.task!.text = value;

    const saveTask: SaveTaskResource = {
      id,
      text: value,
      field: 'text',
      option: WFSaveOptions.MANUAL,
    };

    const values = this.utils.JSONparse(value);

    this.saveTask(
      saveTask,
      false,
      true,
      values,
      this.s.taskId,
      this.s.approveActionId,
      this.s.unapproveActionId
    );
  }

  private handleTextChange(id: number, value: string): void {
    this.task!.text = value;

    const saveTask: SaveTaskResource = {
      id,
      text: value,
      field: 'text',
      option: WFSaveOptions.MANUAL,
    };

    this.saveTask(saveTask, false);
  }

  /**
   * Handle task save triggered by user action, preparing save state and delegating to saveTask.
   * Updates task text conditionally and determines whether to process approval actions.
   * @param e - Event containing optional refresh flag and optional sign indication for approval flow.
   * @param id - Target resource identifier for the save operation.
   * @param taskId - Optional task identifier for approval/validation actions.
   * @param approveId - Optional identifier for approval action.
   * @param unapproveId - Optional identifier for unapproval action.
   */
  save(
    e: any,
    id?: number,
    taskId?: number,
    approveId?: number,
    unapproveId?: number
  ): void {

    if (!id || !this.task) {
      return;
    }

    const refreshChecklist =
      this.currentChecklist?.checklistStatus?.formStatusID === FormStatusEnum.Draft ||
      !!e?.refreshChecklist;

    const refreshTask = !!e?.refreshTask;

    const isShieldingsComponent =
      this.task.componentID === ComponentType.ShieldingsComponent;

    const textValue = this.getTextValue(e, isShieldingsComponent);
    const check = !isShieldingsComponent && e?.sign != null;

    if (!isShieldingsComponent) {
      this.task.text = textValue ?? undefined;
    }

    const saveTaskResource: SaveTaskResource = {
      id,
      text: textValue,
      field: 'text',
      option: WFSaveOptions.MANUAL,
    };

    this.saveTask(
      saveTaskResource,
      refreshChecklist,
      refreshTask,
      check,
      e?.sign,
      taskId,
      approveId,
      unapproveId
    );
  }

  private getTextValue(e: any, isShieldingsComponent: boolean): string | null {
    if (isShieldingsComponent) {
      return e?.text ?? null;
    }

    if (typeof e === 'string') {
      return e;
    }

    return e?.text ?? null;
  }


  /**
   * Persist task changes and optionally trigger validation or approval actions.
   * Updates local task state and emits changes upon successful save.
   * @param saveTaskResource - The task data to persist.
   * @param refreshChecklist - Whether to refresh related data after save.
   * @param refreshTask - Whether connected clients should receive the updated task.
   * @param check - If true, trigger validation or approval flow instead of normal completion.
   * @param e - Event object for check flow.
   * @param taskId - Task identifier for check flow.
   * @param approveId - Identifier for approval action in check flow.
   * @param unapproveId - Identifier for unapproval action in check flow.
   */
  async saveTask(
    saveTaskResource: SaveTaskResource,
    refreshChecklist?: boolean,
    refreshTask?: boolean,
    check?: boolean,
    e?: any,
    taskId?: number,
    approveId?: number,
    unapproveId?: number
  ): Promise<void> {
    if (!this.task) {
      return;
    }

    const usesLocalSaveState = [
      ComponentType.ShieldingsComponent,
      ComponentType.RadMonitorDueDate,
    ].some(componentID => componentID === this.task.componentID);

    try {
      if (!usesLocalSaveState) {
        this.saving = true;
      }
      this.task.isCompleted = this.utils.taskComplete(this.task);
      saveTaskResource.updatedOn = this.task.updatedOn ?? null;

      const task = await firstValueFrom(
        this.checklistsService.saveWFTask(saveTaskResource, refreshChecklist, refreshTask)
      );

      this.setFormDirty(false);

      if (check) {
        this.check(e, taskId, approveId, unapproveId);
        return;
      }

      if (!task) {
        return;
      }

      // Keep the visible task and the checklist tree current after saves.
      this.applyTaskUpdate(task);
      this.publishTask(task);
      this.updateTask.emit(this.task);
    } catch (error) {
      console.error(error);
      this.reportSaveError(error);
      await this.restoreFromServer();
    } finally {
      if (!usesLocalSaveState) {
        this.saving = false;
      }
    }
  }

  /**
   * Pushes a saved task into the store so the other tasks of the checklist re-evaluate against it.
   *
   * A signature condition is read off a sibling task, so the sibling only greys out once the store
   * carries the new signature. The Save endpoint broadcasts over SignalR only when it is asked to
   * refresh the task, which the value saves do not ask for, so publishing here is what makes a
   * signature applied by picking a radio option lock its counterpart straight away.
   * @param task The task as the server returned it.
   */
  private publishTask(task: WfTaskLocalResource) {
    if (!task?.id) return;

    // The server's copy of the task does not always carry the resolved component, and the store is
    // read by the condition parser, which needs it. Resolved the same way setAllTasks does.
    if (!task.component) {
      task.component = this.checklistsService.components?.find(x => x.id === (task.componentID || task.type));
      task.componentID = task.component?.id ?? task.componentID;
    }

    this.store.dispatch(new TasksUpdate(task.id, task));
  }

  /**
   * Shows the reason the server gave when it refused a save or signature. A 409 carries an
   * explanation the user can act on, such as a signature condition blocking the signature or a
   * stale-save conflict, and is worth showing verbatim instead of the generic failure message.
   * @param error The rejected HTTP response.
   */
  private reportSaveError(error: any) {
    const httpError = error as HttpErrorResponse;
    const conflictMessage = httpError?.status === 409 ? httpError.error : null;
    if (typeof conflictMessage === 'string' && conflictMessage.trim())
      this.alert.error(conflictMessage);
    else
      this.alert.defaultError();
  }

  /**
   * Puts the UI back in the server's state after a rejected save or signature. The local task is
   * edited in place before the request goes out, so a refusal would otherwise leave the control
   * showing a value the server never accepted.
   */
  private async restoreFromServer() {
    // The checkbox controls hold their own checked state and only re-read it when a bound input
    // changes value. A refusal usually leaves those inputs exactly as they were, so there is no
    // change to react to and the tick would survive. Rebuilding the control is what clears it.
    this.resetting = true;
    setTimeout(() => this.resetting = false);

    await this.reloadCurrentTask();
  }

  /**
   * Reloads the task after a stale save/sign conflict so the UI shows the latest persisted value.
   * Falls back to a checklist refresh when the task cannot be reloaded directly.
   */
  private async reloadCurrentTask(): Promise<void> {
    const stepID = this.task.wfStepLocalID ?? this.task.wfStepLocal?.id;
    let taskReloaded = false;
    try {
      if (stepID) {
        const tasks = await firstValueFrom(this.checklistsService.getTasksByStepID(stepID));
        const currentTask = tasks.find(task => task.id === this.task.id);
        if (currentTask) {
          currentTask.required = this.task.required;
          this.store.dispatch(new TasksUpdate(currentTask.id ?? 0, currentTask));
          this.refreshSignature(currentTask);
          this.updateTask.emit(currentTask);
          taskReloaded = true;
        }
      }
    } catch (refreshError) {
      console.error(refreshError);
    }
    if (!taskReloaded && this.currentChecklist?.id) {
      this.store.dispatch(new ChecklistsRefreshById(this.currentChecklist.id));
    }
  }

  /**
   * Applies the configured approval action for a checkbox event.
   * Missing event payloads are ignored because some controls emit during visibility refreshes.
   * @param e Boolean or checkbox event emitted by the rendered control.
   * @param taskId Workflow task identifier.
   * @param approveId Approval action identifier.
   * @param unapproveId Unapproval action identifier.
   */
  async check(e: any, taskId?: number, approveId?: number, unapproveId?: number): Promise<void> {
    if (!taskId || e == null)
      return;

    if (e === true || e?.checked === true) {
      if (approveId)
        this.sign(null, taskId, approveId, e?.isASCC);
    } else {
      // Cleared before the signature is removed, not after: the save path stamps the signature, so
      // clearing second would put the initials straight back on.
      await this.clearRadioSelection();
      if (unapproveId)
        this.sign(null, taskId, unapproveId);
      else
        this.refreshSignature();
    }
  }

  /**
   * Drops the radio selection when the user removes their initials, so the answer and the
   * signature are cleared together rather than leaving a selection nobody has signed for.
   *
   * Zero is the empty selection: the builder rejects a radio option whose value is 0, so no option
   * can match it.
   */
  private async clearRadioSelection() {
    // componentID rather than component.id: the task is replaced by the server's response after a
    // save or a sign, and that copy does not always carry the resolved component.
    const componentId = this.task?.componentID ?? this.task?.component?.id;
    if (componentId != ComponentType.CheckboxAndRadio || !this.task.id) return;

    this.task.numericValue = 0;

    // Saved directly rather than through saveTask, which would adopt the response and redraw. The
    // server stamps a signature on every save, so that response still carries the initials this
    // click is removing, and showing it would tick the box back on for a frame. The unapprove that
    // follows is what settles the control.
    try {
      await firstValueFrom(this.checklistsService.saveWFTask({
        id: this.task.id,
        numericValue: 0,
        field: 'numericValue',
        option: WFSaveOptions.MANUAL,
        // Without the concurrency stamp the server reads this save as stale whenever another
        // user was the last to touch the task, and refuses it with a 409.
        updatedOn: this.task.updatedOn ?? null,
      }, false, false));
    } catch (error) {
      console.error(error);
      this.reportSaveError(error);
    }
  }

  isReviewer() {
    return this.checklistsService.isReviewer;
  }

  annotationsChanged(e: any) {
    if (e)
      this.task = e;
  }

  close() {
    if (this.currentChecklist && this.task)
      this.checklistsService.processTags(this.currentChecklist, this.task);
  }

  completed() {
    if (this.task)
      return this.utils.taskComplete(this.task);
    return false;
  }

  stepStatusCorrecting(task: WfTaskLocalResource) {
    return task.wfStepLocal?.wfStepStatusLocals.map(s => s.checklistExecStatus?.formStatusID).includes(FormStatusEnum.Correcting);
  }





}

export class Value {
  index!: number;
  code!: string;
  val!: boolean;
}
